<script setup lang="ts">
import { computed, ref } from "vue";

import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import { convertToMarkdown } from "~/components/text/transformers";
import { XIcon } from "~/icons";
import { Placement } from "~/shared/enums";
import type { Chat } from "~/shared/types";
import { useDataStore } from "~/stores";
import { groupByTimeframe } from "~/utils/date";
import { when } from "~/utils/wait";

const props = defineProps<{
  chatDuid: string;
}>();

const emit = defineEmits<{
  select: [duid: string];
  delete: [duid: string];
}>();

const dataStore = useDataStore();

const dropdownMenu = ref<InstanceType<typeof MultiselectDropdownMenu> | null>(null);

const selectChat = async (chatDuid: string) => {
  await when(computed(() => !dropdownMenu.value?.isOpen));
  emit("select", chatDuid);
};

const getAdtlSearchTerms = (chat: Chat): string[] => {
  const searchTerms = [];

  if (chat.title) {
    searchTerms.push(chat.title);
  }
  if (chat.draft) {
    searchTerms.push(convertToMarkdown(chat.draft.content));
  }
  chat.messages.forEach((message) => {
    searchTerms.push(convertToMarkdown(message.content));
  });

  return searchTerms;
};

const chatSections = computed(() =>
  groupByTimeframe(dataStore.chatList, (chat) => chat.updatedAt).map((timeframe) => ({
    title: timeframe.title,
    showTitle: true,
    items: timeframe.items.map((chat) => {
      const isCurrentChat = chat.duid === props.chatDuid;
      const isEmptyChat = dataStore.isChatEmpty(chat);
      return {
        value: chat.duid,
        label: chat.title ?? chat.updatedAt,
        selected: false,
        disabled: isCurrentChat && isEmptyChat,
        disabledReason: isCurrentChat && isEmptyChat ? "This is your current chat" : undefined,
        adtlSearchTerms: getAdtlSearchTerms(chat),
        component: DropdownMenuItemContent,
        componentArgs: {
          title: chat.title || "New chat",
          isMultiSelect: true,
          disabled: isCurrentChat,
          titleClass: isCurrentChat ? "opacity-50" : undefined,
          showIconAfterOnHover: true,
          iconAfter: isCurrentChat && isEmptyChat ? undefined : XIcon,
          iconAfterArgs: {
            class: "w-4 h-4 text-vlt hover:bg-hvy rounded",
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              emit("delete", chat.duid);
            },
          },
        },
      };
    }),
  }))
);
</script>

<template>
  <MultiselectDropdownMenu
    ref="dropdownMenu"
    placeholder="Search"
    :sections="chatSections"
    header-title="Select chat"
    :placement="Placement.BOTTOM_RIGHT"
    :distance="2"
    :width-pixels="300"
    close-on-select
    @add="selectChat">
    <slot />
  </MultiselectDropdownMenu>
</template>
